import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { format } from 'date-fns';
import { WiThermometer } from 'react-icons/wi';
import { useInterval } from 'beautiful-react-hooks';
import { Color, headerHeight, padding } from '../../constants';
import { Logo } from '../Logo';
import { useWeather } from '../../api/weather-context';
import { useSensorData } from '../../api';
import { WeatherIcon } from '../WeatherIcon';

enum AirQuality {
  GOOD = 'GOOD',
  MODERATE = 'MODERATE',
  BAD = 'BAD',
}
const presentation = {
  [AirQuality.GOOD]: {
    icon: '/svg/smile-face.svg',
  },

  [AirQuality.MODERATE]: {
    icon: '/svg/neutral-face.svg',
  },
  [AirQuality.BAD]: {
    icon: '/svg/frown-face.svg',
  },
};
const useStyles = createUseStyles({
  header: {
    display: 'flex',
    width: '100%',
    height: headerHeight,
    alignItems: 'center',
    justifyContent: 'space-between',
    zIndex: 99999,
    backgroundColor: Color.white,
    padding,
  },
  logo: {
    position: 'absolute',
    width: `calc(4 * ${headerHeight})`,
    height: headerHeight,
    left: `calc(50% - 2 * ${headerHeight})`,
  },
  date: {
    fontFamily: 'Fira-Sans-Medium',
    fontWeight: 600,
    fontSize: 'clamp(1.5rem, 7vh, 16rem)',
    color: Color.b2bBlueDark,
  },
  weather: {
    display: 'flex',
    alignItems: 'center',
    gap: '5rem',
    fontSize: '3rem',
    color: Color.b2bBlueDark,
  },
  group: {
    display: 'flex',
    alignItems: 'center',
  },
  value: {
    marginRight: '1rem',
    fontSize: 'clamp(1.5rem, 7vh, 16rem)',
    fontFamily: 'Fira-Sans-Medium',
    fontWeight: 600,
  },
  unit: {
    fontSize: 'clamp(1.5rem, 3vh, 16rem)',
  },
  image: {
    height: 'clamp(1.5rem, 3.5vw, 16rem)',
    width: 'clamp(1.5rem, 3.5vw, 16rem) !important',
    marginRight: '1rem',
  },
  icon: {
    height: 'clamp(1.2rem, 7vw, 8rem)',
    width: 'clamp(1.2rem, 7vw, 8rem)',
  },
  [AirQuality.GOOD]: {
    color: Color.airQualityGood,
  },
  [AirQuality.MODERATE]: {
    color: Color.airQualityModerate,
  },
  [AirQuality.BAD]: {
    color: Color.airQualityBad,
  },
});

type HeaderProps = {
  co2Id: string;
};

export const Header: React.FC<HeaderProps> = ({ co2Id }) => {
  const { data: co2 } = useSensorData(co2Id);
  const airQuality = useMemo(() => {
    const value = Math.min(2000, co2 || 0);
    if (value > 1200) {
      return AirQuality.BAD;
    }
    if (value > 1000) {
      return AirQuality.MODERATE;
    }
    return AirQuality.GOOD;
  }, [co2]);

  const classes = useStyles();
  const weather = useWeather();

  const [time, setTime] = useState(format(new Date(), 'HH:mm'));
  const tickClock = () => setTime(format(new Date(), 'HH:mm'));
  useInterval(tickClock, 1000);

  if (!weather?.data) return null;
  const { data } = weather;
  const { main, weather: weatherData } = data;

  const temperature = main?.temp || 0;
  // const precipitation = daily?.[0].pop || 0;

  return (
    <header className={classNames(classes.header)}>
      <div className={classes.weather}>
        <div className={classes.group}>
          <WiThermometer className={classes.icon} />
          <div className={classes.value}>{Math.round(temperature)}</div>
          <div className={classes.unit}>{`°C`}</div>
        </div>
        <WeatherIcon type={weatherData![0].icon} className={classes.icon} />
        {/* <div className={classes.precipitation}>
          <WiUmbrella className={classes.icon} />
          <div className={classes.value}>
            {(precipitation * 100).toFixed(0)}
          </div>
          <div className={classes.unit}>{`%`}</div>
        </div> */}
        <div className={classes.group}>
          <img
            src={presentation[airQuality].icon}
            alt="quality"
            className={classes.image}
          />
          <div className={classes.value}>{co2}</div>
          <div className={classes.unit}>ppm</div>
        </div>
      </div>
      <Logo className={classes.logo} />
      <div className={classes.date}>{time}</div>
    </header>
  );
};
